import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { useSetRecoilState, useRecoilState } from "recoil";

import { NoticeRegistContainer } from "style/admin/contents";
import { AdminCalibrationAttachment } from "components/admin/contents/calibration/admin.calibration.attachment";
import AdminCommonModalLayout from "components/admin/common.modal.layout/admin.common.modal";
import { CalibrationCreateModal } from "components/admin/contents/calibration/modal/calibration.create.modal";
import { isCalibrationChangedState, calibrationRegistTriggerState, calibrationCreateModalState } from "atoms/admin/contents/calibration.state"
import { CalibrationCreateParams } from "interface/admin/interface.calibration";
import { fetchCreateAdminCalibration } from "api/admin/contents.calibration";

export function AdminCalibrationRegist() {
  const { register, handleSubmit, control, setValue } = useForm();
  const watchedTitle = useWatch({ control, name: "title" });
  const watchedContents = useWatch({ control, name: "contents" });
  const watchedFiles = useWatch({ control, name: "noticeFiles" });
  const setIsCalibrationChanged = useSetRecoilState(isCalibrationChangedState);
  const [calibrationRegistTrigger, setCalibrationRegistTrigger] = useRecoilState(calibrationRegistTriggerState);
  const [calibrationCreateModal, setCalibrationCreateModal] = useRecoilState(calibrationCreateModalState);

  const onSubmit = async (data: any) => {
    try {
      const params: CalibrationCreateParams = {
        serial: data.title,
        memo: data.contents,
        files: data.noticeFiles?.filter((file: any) => file instanceof File),
      };
      const response = await fetchCreateAdminCalibration(params);
      if (response.result) {
        setCalibrationCreateModal({ isOpen: true, isVisible: true });
        setIsCalibrationChanged(false); // 변경 없음 상태로 설정
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateModalClosed = () => {
    setCalibrationCreateModal({ isOpen: false, isVisible: false });
  };

  useEffect(() => {
    // ✅ 새 파일 감지 (File 객체만 필터링)
    const newFiles =
      watchedFiles?.filter((file: any) => file instanceof File) || [];

    // ✅ 제목과 내용은 둘 다 입력되어야 함
    const isTitleValid = !!watchedTitle && watchedTitle.trim() !== "";
    const isContentsValid = !!watchedContents && watchedContents.trim() !== "";

    // ✅ 제목과 내용이 모두 입력되었을 때만 변경 여부 판단
    const isTextChanged = isTitleValid && isContentsValid;

    // ✅ 파일은 없어도 괜찮음, 추가된 경우만 변경됨
    const isFileChanged = newFiles.length > 0;

    // ✅ 제목과 내용이 입력된 상태에서 파일이 추가되거나 텍스트가 변경되었을 때만 변경 감지
    setIsCalibrationChanged(isTextChanged && (isFileChanged || isTextChanged));
  }, [watchedFiles, watchedTitle, watchedContents]);

  useEffect(() => {
    if (calibrationRegistTrigger) {
      handleSubmit(onSubmit)(); // `onSubmit` 실행
      setCalibrationRegistTrigger(false); // 상태 초기화 (중복 실행 방지)
    }
  }, [calibrationRegistTrigger, handleSubmit]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <NoticeRegistContainer>
        <div className="title-box">
          <input
            type="text"
            {...register("title")}
            placeholder="시리얼번호를 입력하세요."
          />
          <span></span>
        </div>
        <div className="contents-box">
          <Controller
            name="contents"
            control={control}
            rules={{ required: "시리얼번호는 필수입니다." }}
            render={({ field }) => (
              <TextareaAutosize
                {...field}
                minRows={17}
                maxRows={17}
                placeholder="캘리브레이션 메모를 입력하세요."
              />
            )}
          />
          <span></span>
        </div>
        <div className="file-box">
          <AdminCalibrationAttachment
            name="noticeFiles"
            register={register}
            setValue={setValue}
            initialFiles={[]} // ReviewFile[] 그대로 전달
          />
        </div>

        {calibrationCreateModal && (
          <AdminCommonModalLayout
            isVisible={calibrationCreateModal.isVisible}
            closedHandler={handleCreateModalClosed}
          >
            <CalibrationCreateModal
              closedHandler={handleCreateModalClosed}
            />
          </AdminCommonModalLayout>
        )}
      </NoticeRegistContainer>
    </form>
  );
}
